import type { WebGPURenderer } from 'three/webgpu';

export interface StatsSnapshot {
  fps: number;
  cpuMs: number;
  gpuMs: number;
  computeMs: number;
  draws: number;
  triangles: number;
}

/**
 * Lightweight stats overlay: FPS, CPU frame time, GPU render/compute time (when
 * the adapter supports timestamp queries), draw calls and triangles, plus any
 * custom lines systems want to publish.
 */
export class Stats {
  private readonly el: HTMLElement;
  private frames = 0;
  private cpuAccum = 0;
  private lastReport = performance.now();
  private gpuRender = 0;
  private gpuCompute = 0;
  private readonly extra = new Map<string, string>();
  private last: StatsSnapshot = { fps: 0, cpuMs: 0, gpuMs: 0, computeMs: 0, draws: 0, triangles: 0 };
  visible = true;

  constructor(private readonly renderer: WebGPURenderer) {
    this.el = document.getElementById('stats')!;
  }

  /** Publish a custom line, e.g. stats.set('tiles', '812 / 961'). */
  set(key: string, value: string): void {
    this.extra.set(key, value);
  }

  /** The most recent report (for automated benchmarks). */
  snapshot(): StatsSnapshot {
    return { ...this.last };
  }

  setVisible(v: boolean): void {
    this.visible = v;
    this.el.style.display = v ? 'block' : 'none';
  }

  /** Call once per frame after rendering, with the CPU time spent on the frame. */
  frame(cpuMs: number): void {
    this.frames++;
    this.cpuAccum += cpuMs;
    const r = this.renderer as unknown as {
      backend: { trackTimestamp?: boolean };
      resolveTimestampsAsync(type: string): Promise<number | undefined>;
    };
    if (r.backend.trackTimestamp) {
      r.resolveTimestampsAsync('render').then((ms) => {
        if (typeof ms === 'number') this.gpuRender = this.gpuRender * 0.9 + ms * 0.1;
      });
      r.resolveTimestampsAsync('compute').then((ms) => {
        if (typeof ms === 'number') this.gpuCompute = this.gpuCompute * 0.9 + ms * 0.1;
      });
    }
    const now = performance.now();
    if (now - this.lastReport < 250) return;
    const secs = (now - this.lastReport) / 1000;
    const fps = this.frames / secs;
    const cpu = this.cpuAccum / this.frames;
    this.frames = 0;
    this.cpuAccum = 0;
    this.lastReport = now;
    const info = this.renderer.info.render;
    this.last = {
      fps,
      cpuMs: cpu,
      gpuMs: this.gpuRender,
      computeMs: this.gpuCompute,
      draws: info.drawCalls,
      triangles: info.triangles,
    };
    if (!this.visible) return;
    const lines = [
      `${fps.toFixed(0)} fps  cpu ${cpu.toFixed(1)} ms`,
      r.backend.trackTimestamp
        ? `gpu ${this.gpuRender.toFixed(1)} ms  compute ${this.gpuCompute.toFixed(2)} ms`
        : 'gpu timing n/a',
      `draws ${info.drawCalls}  tris ${(info.triangles / 1e6).toFixed(2)}M`,
    ];
    for (const [k, v] of this.extra) lines.push(`${k} ${v}`);
    this.el.textContent = lines.join('\n');
  }
}
